import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarIcon from '../../../../assets/Admin/Payout/calendar.svg';
import ReactDOM from 'react-dom';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import './EditTransactionHistory.css';

const EditTransactionHistory = ({
  open,
  handleClose,
  transaction,
  onSave,
  organizationId,
  canManagePayouts = false,
}) => {
  const [editedTransaction, setEditedTransaction] = useState({
    start_date: '',
    end_date: '',
    transaction_id: '',
    transaction_date: '',
    total_payout_amount: '',
    bank_deduction_amount: '',
    actual_amount: '',
  });

  const [loading, setLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDateRange, setSelectedDateRange] = useState({ start: null, end: null });
  const [activeDateField, setActiveDateField] = useState(null); // 'start_date' or 'end_date'
  const startDateButtonRef = useRef(null);
  const endDateButtonRef = useRef(null);
  const isAddMode = !transaction?.id;
  const [isSelectingStart, setIsSelectingStart] = useState(true);
  const [showTransactionDateCalendar, setShowTransactionDateCalendar] = useState(false);
  const transactionDateButtonRef = useRef(null);
  const [payoutData, setPayoutData] = useState(null);

  // Fetch payout data when component mounts or organizationId changes
  useEffect(() => {
    const fetchPayoutData = async () => {
      if (!organizationId) return;

      try {
        const response = await axiosInstance.get(
          `/admin/organisation/total-earnings/${organizationId}`
        );
        setPayoutData(response.data);
      } catch (error) {
        console.error('Error fetching payout data:', error);
        // Don't show toast for this error as it's not critical
      }
    };

    if (open) {
      fetchPayoutData();
    }
  }, [organizationId, open]);

  useEffect(() => {
    if (transaction) {
      setEditedTransaction({
        start_date: transaction.start_date || '',
        end_date: transaction.end_date || '',
        transaction_id: transaction.transaction_id || '',
        transaction_date: transaction.transaction_date || '',
        total_payout_amount: transaction.total_payout_amount || '',
        bank_deduction_amount: transaction.bank_deduction_amount || '',
        actual_amount: transaction.actual_amount || '',
      });
    } else {
      // Reset form for add mode
      setEditedTransaction({
        start_date: '',
        end_date: '',
        transaction_id: '',
        transaction_date: '',
        total_payout_amount: '',
        bank_deduction_amount: '',
        actual_amount: '',
      });
    }
  }, [transaction, open]);

  // Calendar helper functions
  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add days from previous month
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }

    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const handleDateSelect = date => {
    if (!canManagePayouts) {
      toast.error("You don't have permission to manage payouts");
      return;
    }
    if (isSelectingStart) {
      setSelectedDateRange({ start: date, end: null });
      setIsSelectingStart(false);
    } else {
      if (date >= selectedDateRange.start) {
        setSelectedDateRange({ ...selectedDateRange, end: date });
        setIsSelectingStart(true);
      } else {
        setSelectedDateRange({ start: date, end: selectedDateRange.start });
        setIsSelectingStart(true);
      }
    }
  };

  const handleConfirmDateRange = () => {
    if (!canManagePayouts) {
      toast.error("You don't have permission to manage payouts");
      return;
    }
    if (selectedDateRange.start && selectedDateRange.end) {
      setShowCalendar(false);
      // Format as DD-MM-YYYY
      const format = d =>
        `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${d.getFullYear()}`;
      setEditedTransaction({
        ...editedTransaction,
        start_date: format(selectedDateRange.start),
        end_date: format(selectedDateRange.end),
      });
    }
  };

  const handleCancelDateRange = () => {
    setSelectedDateRange({ start: null, end: null });
    setIsSelectingStart(true);
    setShowCalendar(false);
  };

  const isDateInRange = date => {
    if (!selectedDateRange.start || !selectedDateRange.end) return false;
    return date >= selectedDateRange.start && date <= selectedDateRange.end;
  };

  const isDateSelected = date => {
    if (!selectedDateRange.start) return false;
    if (selectedDateRange.end) {
      return (
        date.getTime() === selectedDateRange.start.getTime() ||
        date.getTime() === selectedDateRange.end.getTime()
      );
    }
    return date.getTime() === selectedDateRange.start.getTime();
  };

  const handlePrevMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        showCalendar &&
        (startDateButtonRef.current || endDateButtonRef.current) &&
        !startDateButtonRef.current?.contains(event.target) &&
        !endDateButtonRef.current?.contains(event.target)
      ) {
        const calendarElement = document.querySelector('.eth-custom-calendar');
        if (calendarElement && !calendarElement.contains(event.target)) {
          setShowCalendar(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const handleChange = field => event => {
    if (!canManagePayouts) {
      toast.error("You don't have permission to manage payouts");
      return;
    }
    const value = event.target.value;

    const nextTransaction = {
      ...editedTransaction,
      [field]: value,
    };

    if (field === 'total_payout_amount' || field === 'bank_deduction_amount') {
      const total = parseFloat(
        field === 'total_payout_amount' ? value : nextTransaction.total_payout_amount
      );
      const bank = parseFloat(
        field === 'bank_deduction_amount' ? value : nextTransaction.bank_deduction_amount
      );

      if (!isNaN(total) && !isNaN(bank)) {
        nextTransaction.actual_amount = (total - bank).toFixed(2);
      } else {
        nextTransaction.actual_amount = '';
      }
    }

    setEditedTransaction(nextTransaction);
  };

  // Date formatting helper
  const formatDateForAPI = dateString => {
    if (!dateString) return '';
    // Convert DD-MM-YYYY to YYYY-MM-DD for API
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
  };

  // Date formatting helper for display
  const formatDateForDisplay = dateString => {
    if (!dateString) return '';
    // Convert YYYY-MM-DD to DD-MM-YYYY for display
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
  };

  // Special handler for date formatting
  const handleDateChange = field => event => {
    if (!canManagePayouts) {
      toast.error("You don't have permission to manage payouts");
      return;
    }
    let value = event.target.value;

    // Remove non-numeric and non-hyphen characters
    value = value.replace(/[^\d-]/g, '');

    // Split the input by hyphens to get day, month, year parts
    const parts = value.split('-');

    let day = parts[0] ? parseInt(parts[0], 10) : 0;
    let month = parts[1] ? parseInt(parts[1], 10) : 0;
    let year = parts[2] ? parseInt(parts[2], 10) : 0;

    // Format the day part (first two characters)
    if (parts[0]) {
      if (parts[0].length === 1 && parseInt(parts[0]) > 3) {
        parts[0] = `0${parts[0]}`;
        day = parseInt(parts[0], 10);
      } else if (parts[0].length > 2) {
        parts[0] = parts[0].substring(0, 2);
        day = parseInt(parts[0], 10);
      }
    }

    // Format the month part (characters after first hyphen)
    if (parts[1]) {
      if (parts[1].length === 1 && parseInt(parts[1]) > 1) {
        parts[1] = `0${parts[1]}`;
        month = parseInt(parts[1], 10);
      } else if (parts[1].length > 2) {
        parts[1] = parts[1].substring(0, 2);
        month = parseInt(parts[1], 10);
      }

      // Ensure month is valid (between 01 and 12)
      if (parts[1].length === 2 && (month < 1 || month > 12)) {
        parts[1] = month < 1 ? '01' : '12';
        month = parseInt(parts[1], 10);
      }
    }

    // Limit year to 4 digits
    if (parts[2] && parts[2].length > 4) {
      parts[2] = parts[2].substring(0, 4);
      year = parseInt(parts[2], 10);
    }

    // Validate day based on month and year if we have all parts
    if (parts[0] && parts[0].length === 2 && parts[1] && parts[1].length === 2 && year) {
      const maxDaysInMonth = new Date(year, month, 0).getDate();

      if (day > maxDaysInMonth) {
        parts[0] = maxDaysInMonth.toString().padStart(2, '0');
      } else if (day < 1) {
        parts[0] = '01';
      }
    }
    // If we don't have the year yet but have day and month
    else if (parts[0] && parts[0].length === 2 && parts[1] && parts[1].length === 2) {
      // Use current year for validation until user enters a year
      const currentYear = new Date().getFullYear();
      const maxDaysInMonth = new Date(currentYear, month, 0).getDate();

      if (day > maxDaysInMonth) {
        parts[0] = maxDaysInMonth.toString().padStart(2, '0');
      } else if (day < 1) {
        parts[0] = '01';
      }
    }

    // Automatically add hyphens as needed
    if (parts[0] && parts[0].length === 2 && !value.includes('-')) {
      value = `${parts[0]}-`;
    } else if (parts[0] && parts[1] && parts[1].length === 2 && value.split('-').length === 2) {
      value = `${parts[0]}-${parts[1]}-`;
    } else {
      value = parts.filter(Boolean).join('-');
    }

    setEditedTransaction({
      ...editedTransaction,
      [field]: value,
    });
  };

  const validateForm = () => {
    if (!canManagePayouts) {
      toast.error("You don't have permission to manage payouts");
      return false;
    }
    const requiredFields = [
      'start_date',
      'end_date',
      'transaction_id',
      'transaction_date',
      'total_payout_amount',
      'bank_deduction_amount',
      'actual_amount',
    ];
    const missingFields = requiredFields.filter(field => !editedTransaction[field]);

    if (missingFields.length > 0) {
      toast.error('Please fill in all required fields');
      return false;
    }

    // Validate date format
    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (
      !dateRegex.test(editedTransaction.start_date) ||
      !dateRegex.test(editedTransaction.end_date) ||
      !dateRegex.test(editedTransaction.transaction_date)
    ) {
      toast.error('Please enter dates in DD-MM-YYYY format');
      return false;
    }

    // Validate amounts
    const totalAmount = parseFloat(editedTransaction.total_payout_amount);
    const bankDeduction = parseFloat(editedTransaction.bank_deduction_amount);
    const actualAmount = parseFloat(editedTransaction.actual_amount);

    if (isNaN(totalAmount) || isNaN(bankDeduction) || isNaN(actualAmount)) {
      toast.error('Please enter valid amounts');
      return false;
    }

    if (totalAmount < bankDeduction) {
      toast.error('Bank deduction cannot be greater than total payout amount');
      return false;
    }

    if (Math.abs(totalAmount - bankDeduction - actualAmount) > 0.01) {
      toast.error('Actual amount should equal total payout amount minus bank deduction');
      return false;
    }

    // Validate against available payout
    if (payoutData) {
      const max = payoutData.available_for_payout * 7.5;
      if (totalAmount > max || actualAmount > max) {
        toast.error(`Transaction amount cannot exceed available payout of QR ${max.toFixed(2)}`);
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        start_date: formatDateForAPI(editedTransaction.start_date),
        end_date: formatDateForAPI(editedTransaction.end_date),
        transaction_id: editedTransaction.transaction_id,
        transaction_date: formatDateForAPI(editedTransaction.transaction_date),
        total_payout_amount: parseFloat(editedTransaction.total_payout_amount),
        bank_deduction_amount: parseFloat(editedTransaction.bank_deduction_amount),
        actual_amount: parseFloat(editedTransaction.actual_amount),
      };

      if (isAddMode) {
        // Add transaction
        await axiosInstance.post(`/admin/organisation/add-transaction/${organizationId}`, payload);
        toast.success('Transaction added successfully');
      } else {
        // Edit transaction
        await axiosInstance.patch(`/admin/organisation/transaction/${transaction.id}`, payload);
        toast.success('Transaction updated successfully');
      }

      onSave(editedTransaction);
      handleClose();
    } catch (error) {
      console.error('Error saving transaction:', error);

      if (error.response) {
        const status = error.response.status;
        const errorMessage = error.response.data?.detail || 'Failed to save transaction';

        if (status === 400) {
          toast.error(errorMessage);
        } else if (status === 401 || status === 403) {
          toast.error('You are not authorized to perform this action');
        } else if (status === 404) {
          toast.error('Transaction not found');
        } else {
          toast.error(`Error: ${errorMessage}`);
        }
      } else if (error.request) {
        toast.error('Network error. Please check your internet connection');
      } else {
        toast.error('An unexpected error occurred. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to open calendar for a specific date field
  const openCalendarForField = field => {
    if (!canManagePayouts) {
      toast.error("You don't have permission to manage payouts");
      return;
    }
    setActiveDateField(field);
    setShowCalendar(true);
    // Reset date selection when opening for a new field
    setSelectedDateRange({ start: null, end: null });
  };

  // Render custom calendar
  const renderCustomCalendar = () => {
    if (!showCalendar) return null;

    const days = getDaysInMonth(calendarDate);
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    // Get button position for calendar positioning
    let buttonRect;
    const calendarWidth = 280;
    const calendarHeight = 320; // Approximate height
    const padding = 8;

    if (activeDateField === 'start_date') {
      buttonRect = startDateButtonRef.current?.getBoundingClientRect();
    } else if (activeDateField === 'end_date') {
      buttonRect = endDateButtonRef.current?.getBoundingClientRect();
    }

    // Default positioning if no button found
    let calendarLeft = 100;
    let calendarTop = 100;

    if (buttonRect) {
      // Calculate position to ensure calendar is visible
      calendarLeft = buttonRect.left;
      calendarTop = buttonRect.bottom + padding;

      // Check if calendar would go off the right edge
      if (calendarLeft + calendarWidth > window.innerWidth) {
        calendarLeft = window.innerWidth - calendarWidth - padding;
      }

      // Check if calendar would go off the bottom edge
      if (calendarTop + calendarHeight > window.innerHeight) {
        calendarTop = buttonRect.top - calendarHeight - padding;
      }

      // Ensure calendar doesn't go off the left edge
      if (calendarLeft < padding) {
        calendarLeft = padding;
      }

      // Ensure calendar doesn't go off the top edge
      if (calendarTop < padding) {
        calendarTop = padding;
      }
    }

    const calendar = (
      <div
        className="eth-custom-calendar"
        style={{
          position: 'fixed',
          left: `${calendarLeft}px`,
          top: `${calendarTop}px`,
          zIndex: 9999,
        }}
      >
        <div className="eth-calendar-header">
          <button onClick={handlePrevMonth} className="eth-calendar-nav-btn">
            &lt;
          </button>
          <div className="eth-calendar-title">
            {`${monthNames[calendarDate.getMonth()]} ${calendarDate.getFullYear()}`}
          </div>
          <button onClick={handleNextMonth} className="eth-calendar-nav-btn">
            &gt;
          </button>
        </div>

        <div className="eth-calendar-days-header">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>

        <div className="eth-calendar-grid">
          {days.map((day, index) => (
            <div
              key={index}
              className={`eth-calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} 
                         ${isDateSelected(day.date) ? 'selected' : ''} 
                         ${isDateInRange(day.date) ? 'in-range' : ''}`}
              onClick={() => handleDateSelect(day.date)}
            >
              {day.day}
            </div>
          ))}
        </div>

        <div className="eth-calendar-actions">
          <button onClick={handleCancelDateRange} className="eth-calendar-cancel-btn">
            Cancel
          </button>
          <button
            onClick={handleConfirmDateRange}
            className="eth-calendar-confirm-btn"
            disabled={!selectedDateRange.start || !selectedDateRange.end}
          >
            Confirm
          </button>
        </div>
      </div>
    );

    return ReactDOM.createPortal(calendar, document.body);
  };

  // Transaction Date Calendar logic
  const renderTransactionDateCalendar = () => {
    if (!showTransactionDateCalendar) return null;
    const days = getDaysInMonth(calendarDate);
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    // Get button position for calendar positioning
    let buttonRect = transactionDateButtonRef.current?.getBoundingClientRect();
    const calendarWidth = 280;
    const calendarHeight = 320;
    const padding = 8;
    let calendarLeft = 100;
    let calendarTop = 100;
    if (buttonRect) {
      calendarLeft = buttonRect.left;
      calendarTop = buttonRect.bottom + padding;
      if (calendarLeft + calendarWidth > window.innerWidth) {
        calendarLeft = window.innerWidth - calendarWidth - padding;
      }
      if (calendarTop + calendarHeight > window.innerHeight) {
        calendarTop = buttonRect.top - calendarHeight - padding;
      }
      if (calendarLeft < padding) {
        calendarLeft = padding;
      }
      if (calendarTop < padding) {
        calendarTop = padding;
      }
    }
    // Only allow dates from end_date (inclusive) onwards
    let minDate = null;
    if (editedTransaction.end_date) {
      const [ed, em, ey] = editedTransaction.end_date.split('-').map(Number);
      minDate = new Date(ey, em - 1, ed);
    }
    const isDateDisabled = date => {
      if (!minDate) return false;
      return date < minDate;
    };
    const isDateSelected = date => {
      if (!editedTransaction.transaction_date) return false;
      const [td, tm, ty] = editedTransaction.transaction_date.split('-').map(Number);
      const tDate = new Date(ty, tm - 1, td);
      return date.getTime() === tDate.getTime();
    };
    const handleTransactionDateSelect = date => {
      if (!canManagePayouts) {
        toast.error("You don't have permission to manage payouts");
        return;
      }
      if (isDateDisabled(date)) return;
      const format = d =>
        `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${d.getFullYear()}`;
      setEditedTransaction({
        ...editedTransaction,
        transaction_date: format(date),
      });
      setShowTransactionDateCalendar(false);
    };
    const calendar = (
      <div
        className="eth-transaction-date-calendar"
        style={{
          position: 'fixed',
          left: `${calendarLeft}px`,
          top: `${calendarTop}px`,
          zIndex: 2001,
        }}
      >
        <div className="eth-transaction-calendar-header">
          <button onClick={handlePrevMonth} className="eth-transaction-calendar-nav-btn">
            &lt;
          </button>
          <div className="eth-transaction-calendar-title">{`${monthNames[calendarDate.getMonth()]} ${calendarDate.getFullYear()}`}</div>
          <button onClick={handleNextMonth} className="eth-transaction-calendar-nav-btn">
            &gt;
          </button>
        </div>
        <div className="eth-transaction-calendar-days-header">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>
        <div className="eth-transaction-calendar-grid">
          {days.map((day, index) => (
            <div
              key={index}
              className={`eth-transaction-calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isDateSelected(day.date) ? 'selected' : ''} ${isDateDisabled(day.date) ? 'disabled' : ''}`}
              onClick={() => handleTransactionDateSelect(day.date)}
              style={{
                pointerEvents: isDateDisabled(day.date) ? 'none' : 'auto',
                opacity: isDateDisabled(day.date) ? 0.4 : 1,
              }}
            >
              {day.day}
            </div>
          ))}
        </div>
        <div className="eth-transaction-calendar-actions">
          <button
            onClick={() => setShowTransactionDateCalendar(false)}
            className="eth-transaction-calendar-cancel-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    );
    return ReactDOM.createPortal(calendar, document.body);
  };

  // Add click outside logic for transaction date calendar
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        showTransactionDateCalendar &&
        transactionDateButtonRef.current &&
        !transactionDateButtonRef.current.contains(event.target)
      ) {
        const calendarElement = document.querySelector('.eth-transaction-date-calendar');
        if (calendarElement && !calendarElement.contains(event.target)) {
          setShowTransactionDateCalendar(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTransactionDateCalendar]);

  const maxPayoutAmount = payoutData ? payoutData.available_for_payout * 7.5 : null;
  const isFormValid = (() => {
    if (!canManagePayouts) return false;
    const requiredFields = [
      'start_date',
      'end_date',
      'transaction_id',
      'transaction_date',
      'total_payout_amount',
      'bank_deduction_amount',
      'actual_amount',
    ];
    if (requiredFields.some(field => !editedTransaction[field])) return false;

    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (
      !dateRegex.test(editedTransaction.start_date) ||
      !dateRegex.test(editedTransaction.end_date) ||
      !dateRegex.test(editedTransaction.transaction_date)
    ) {
      return false;
    }

    const totalAmount = parseFloat(editedTransaction.total_payout_amount);
    const bankDeduction = parseFloat(editedTransaction.bank_deduction_amount);
    const actualAmount = parseFloat(editedTransaction.actual_amount);

    if (isNaN(totalAmount) || isNaN(bankDeduction) || isNaN(actualAmount)) return false;
    if (totalAmount < bankDeduction) return false;
    if (Math.abs(totalAmount - bankDeduction - actualAmount) > 0.01) return false;

    if (maxPayoutAmount !== null) {
      if (totalAmount > maxPayoutAmount) return false;
      if (actualAmount > maxPayoutAmount) return false;
    }

    return true;
  })();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      className="edit-transaction-dialog"
    >
      <Box className="eth-dialog-header">
        <DialogTitle className="eth-dialog-title">
          {isAddMode ? 'Add Transaction' : 'Edit Transaction history'}
        </DialogTitle>
        <IconButton onClick={handleClose} className="eth-close-button">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent className="eth-dialog-content">
        {!canManagePayouts && (
          <Box className="eth-permission-banner">
            <Typography className="eth-permission-title">No access to manage payouts</Typography>
            <Typography className="eth-permission-text">
              You do not have the required permission to add or edit payouts.
            </Typography>
          </Box>
        )}
        {/* Max Payout Note */}
        {payoutData && (
          <Box className="eth-max-payout-note">
            <Typography className="eth-max-payout-text">
              <strong>Note:</strong> Maximum payout possible is QR{' '}
              {(payoutData.available_for_payout * 7.5).toFixed(2)}
            </Typography>
          </Box>
        )}

        <Box className="eth-transaction-table">
          <table className="eth-edit-table">
            <colgroup>
              <col style={{ width: '26%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="eth-date-range-header">Date Range</th>
                <th>Transaction ID</th>
                <th>Transaction Date</th>
                <th>Transaction Amount</th>
                <th>Bank Deduction</th>
                <th>Actual Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td data-label="Date Range">
                  <Box className="eth-date-range-container">
                    <button
                      className="eth-date-range-button eth-date-range-merged"
                      onClick={() => openCalendarForField('date_range')}
                      disabled={!canManagePayouts}
                      ref={startDateButtonRef}
                    >
                      <img src={CalendarIcon} alt="Calendar" className="eth-calendar-icon" />
                      <span className="eth-date-range-text">
                        {(() => {
                          const { start_date, end_date } = editedTransaction;
                          if (!start_date && !end_date) return 'Select Date Range';
                          const format = dateStr => {
                            if (!dateStr) return '';
                            const [dd, mm, yyyy] = dateStr.split('-');
                            const months = [
                              'Jan',
                              'Feb',
                              'Mar',
                              'Apr',
                              'May',
                              'Jun',
                              'Jul',
                              'Aug',
                              'Sep',
                              'Oct',
                              'Nov',
                              'Dec',
                            ];
                            return `${dd} ${months[parseInt(mm, 10) - 1]}`;
                          };
                          if (start_date && end_date) {
                            const [, , y1] = start_date.split('-');
                            const [, , y2] = end_date.split('-');
                            if (y1 === y2) {
                              return `${format(start_date)} - ${format(end_date)} ${y1}`;
                            } else {
                              return `${format(start_date)} ${y1} - ${format(end_date)} ${y2}`;
                            }
                          } else if (start_date) {
                            const [, , y1] = start_date.split('-');
                            return `${format(start_date)} - ? ${y1}`;
                          } else if (end_date) {
                            const [, , y2] = end_date.split('-');
                            return `? - ${format(end_date)} ${y2}`;
                          }
                          return '';
                        })()}
                      </span>
                    </button>
                  </Box>
                </td>
                <td data-label="Transaction ID">
                  <TextField
                    value={editedTransaction.transaction_id}
                    onChange={handleChange('transaction_id')}
                    variant="outlined"
                    fullWidth
                    className="eth-edit-input"
                    placeholder={isAddMode ? 'Enter ID' : ''}
                    disabled={!canManagePayouts}
                  />
                </td>
                <td data-label="Transaction Date">
                  <Box className="eth-transaction-date-container">
                    <button
                      className="eth-transaction-date-button"
                      onClick={() => setShowTransactionDateCalendar(true)}
                      ref={transactionDateButtonRef}
                      disabled={!canManagePayouts}
                    >
                      <img src={CalendarIcon} alt="Calendar" className="eth-calendar-icon" />
                      <span className="eth-transaction-date-text">
                        {(() => {
                          if (!editedTransaction.transaction_date) return 'Select Date';
                          const [dd, mm, yyyy] = editedTransaction.transaction_date.split('-');
                          const months = [
                            'Jan',
                            'Feb',
                            'Mar',
                            'Apr',
                            'May',
                            'Jun',
                            'Jul',
                            'Aug',
                            'Sep',
                            'Oct',
                            'Nov',
                            'Dec',
                          ];
                          return `${dd} ${months[parseInt(mm, 10) - 1]} ${yyyy}`;
                        })()}
                      </span>
                    </button>
                  </Box>
                </td>
                <td data-label="Transaction Amount">
                  <TextField
                    value={editedTransaction.total_payout_amount}
                    onChange={handleChange('total_payout_amount')}
                    variant="outlined"
                    fullWidth
                    className="eth-edit-input"
                    type="number"
                    placeholder={isAddMode ? 'Enter amount' : ''}
                    disabled={!canManagePayouts}
                  />
                </td>
                <td data-label="Bank Deduction">
                  <TextField
                    value={editedTransaction.bank_deduction_amount}
                    onChange={handleChange('bank_deduction_amount')}
                    variant="outlined"
                    fullWidth
                    className="eth-edit-input"
                    type="number"
                    placeholder={isAddMode ? 'Enter deduction' : ''}
                    disabled={!canManagePayouts}
                  />
                </td>
                <td data-label="Actual Amount">
                  <TextField
                    value={editedTransaction.actual_amount}
                    onChange={handleChange('actual_amount')}
                    variant="outlined"
                    fullWidth
                    className="eth-edit-input"
                    type="number"
                    placeholder={isAddMode ? 'Enter actual amount' : ''}
                    disabled={!canManagePayouts}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
        <Box className="eth-action-buttons">
          <Button
            onClick={handleClose}
            variant="outlined"
            className="eth-cancel-button"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className="eth-save-button"
            disabled={loading || !isFormValid}
          >
            {loading ? 'Saving...' : isAddMode ? 'Add' : 'Save'}
          </Button>
        </Box>
      </DialogContent>
      {renderCustomCalendar()}
      {renderTransactionDateCalendar()}
    </Dialog>
  );
};

export default EditTransactionHistory;
